<template>
    <div>
        <draggable
            :value="contentSections"
            @input="(data) => $emit('input', data)"
            ghost-class="opacity-50"
            handle=".handle"
            @choose="froalaStore.disableFroala = true"
            @unchoose="froalaStore.disableFroala = false"
        >
            <div
                v-for="(section, index) in contentSections"
                :key="`key_content_block_${contentBlockIndex}_section_${index}`"
                class="my-2 bg-white dark:bg-gray-700 rounded-lg text-black dark:text-white shadow dark:shadow-gray-500 relative"
            >
                <div class="flex justify-between items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 p-4"
                     @click="toggleContentSectionCollapse(index)">
                    <div class="handle">
                        <svg
                            class="w-6 h-6 inline-block"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M4 6h16M4 12h16M4 18h16"
                            ></path>
                        </svg>

                        <span v-if="debugStore.debugMode && section.id" class="font-mono">
                            [ContentSection:{{ section.id }}]
                        </span>
                    </div>
                    <div>
                        {{ section.heading_title }}
                    </div>
                    <div>
                        <svg
                            v-if="!section.collapsed"
                            class="h-4 w-4 cursor-pointer"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M19 9l-7 7-7-7"
                            />
                        </svg>
                        <svg
                            v-if="section.collapsed"
                            class="h-4 w-4 cursor-pointer"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M5 15l7-7 7 7"
                            />
                        </svg>
                    </div>
                </div>
                <div class="px-4 pb-4" v-show="section.collapsed">
                    <div class="relative">
                        <button
                            :disabled="disabled"
                            type="button"
                            v-if="contentSections.length != 1"
                            class="absolute top-0 right-0 bg-red-500 text-white flex flex-row items-center rounded px-3 py-2"
                            @click="$emit('delete-content-section', index)"
                        >
                            <svg
                                class="w-4 h-4"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                            </svg>
                            <span class="ml-2">Delete Section</span>
                        </button>
                    </div>
                    <content-section
                        :disabled="disabled"
                        :gallery="gallery"
                        :language="language"
                        :should-show-market-data="shouldShowMarketData"
                        :additional-casinos-component="additionalCasinosComponent"
                        :is-payment-category-page="isPaymentCategoryPage"
                        :payment-method-id="paymentMethodId"
                        :errors="errors"
                        :content-block-index="contentBlockIndex"
                        :document-class="documentClass"
                        :document-id="documentId"
                        :document-data-id="documentDataId"
                        :document-type-id="documentTypeId"
                        :has-bonus-lists="hasBonusLists"
                        :has-ranking="hasRanking"
                        v-model="contentSections[index]"
                        :index="index"
                        :content-section-with-how-to="contentSectionWithHowTo"
                        :content-section-with-bonus-list-top="contentSectionWithBonusListTop"
                        :content-section-with-bonus-list-bottom="contentSectionWithBonusListBottom"
                        :content-sections-with-ranking="contentSectionsWithRanking"
                        @add-how-to="(uid) => $emit('add-how-to', uid)"
                        @delete-how-to="(uid) => $emit('delete-how-to', uid)"
                        @add-bonus-list-top="(uid) => $emit('add-bonus-list-top', uid)"
                        @delete-bonus-list-top="(uid) => $emit('delete-bonus-list-top', uid)"
                        @add-bonus-list-bottom="(uid) => $emit('add-bonus-list-bottom', uid)"
                        @delete-bonus-list-bottom="(uid) => $emit('delete-bonus-list-bottom', uid)"
                        @add-ranking="(uid) => $emit('add-ranking', uid)"
                        @delete-ranking="(uid) => $emit('delete-ranking', uid)"
                    />
                </div>
            </div>
        </draggable>
    </div>
</template>

<script>
import draggable from "vuedraggable";
import ContentSection from "./ContentSection";
import siteLanguage from '../../config/siteLanguage'
import {useDebugStore} from "../../stores/debug";
import {useFroalaStore} from "../../stores/froala";

export default {
    name: "ContentSectionList",
    components: { ContentSection, draggable },
    props: {
        gallery: Array,
        value: {
            type: Array,
            required: true,
        },
        contentBlockIndex: Number,
        errors: {
            type: [Array, Object],
            required: true,
        },
        language: {
            type: Object,
            default: () => {
                return { iso: siteLanguage.iso.ENGLISH };
            },
        },
        documentClass: {
            type: String,
            required: true,
        },
        documentId: {
            type: Number,
            required: false,
        },
        documentDataId: {
            type: Number,
            required: false,
        },
        documentTypeId: {
            type: Number,
            required: false,
        },
        hasBonusLists: {
            type: Boolean,
            required: false,
            default: true,
        },
        hasRanking: {
            type: Boolean,
            required: false,
            default: true,
        },
        shouldShowMarketData: {
           type : Boolean,
           default : false
        },
        additionalCasinosComponent: {
            type: Object,
            default: () => {
                return { shouldShow: false };
            },
        },
        isPaymentCategoryPage: {
            type: Boolean,
            default: false,
        },
        paymentMethodId: {
            type: Number,
            required: false,
        },
        showAllContent: {
            type: Boolean,
            required: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        contentSectionWithHowTo: {
            type: String,
            required: false,
        },
        contentSectionWithBonusListTop: {
            type: String,
            required: false,
        },
        contentSectionWithBonusListBottom: {
            type: String,
            required: false,
        },
        contentSectionsWithRanking: {
            type: Array,
            required: false,
        },
    },
    data: function () {
        return {
            contentSections: this.value,
            debugStore: useDebugStore(),
            froalaStore: useFroalaStore(),
        };
    },
    mounted: function () {
        this.contentSections.map((section) => {
            section.collapsed = false;
            return section;
        });
    },
    methods: {
        toggleContentSectionCollapse: function (contentSectionIndex) {
            const contentSections = [...this.contentSections];
            contentSections[contentSectionIndex].collapsed = !contentSections[
                contentSectionIndex
            ].collapsed;
            this.contentSections = contentSections;
        },
        toggleAllContentSectionsCollapse: function () {
            const contentSections = [...this.contentSections];
            if (this.showAllContent) {
                contentSections.map(
                    (contentSection) => (contentSection.collapsed = true)
                );
            } else {
                contentSections.map(
                    (contentSection) => (contentSection.collapsed = false)
                );
            }
            this.contentSections = contentSections;
        },
    },
    watch: {
        value: function (newValue) {
            this.contentSections = newValue;
        },
        errors: function(newValue){
            Object.keys(newValue)
                .map(key => key.split('.'))
                .filter(key => {
                    return key[0] === 'contentBlocks' && parseInt(key[1]) === this.contentBlockIndex;
                })
                .forEach(key => {
                    let field = key[2], index = key[3];
                    if (field === 'sections') {
                        this.$set(this.contentSections[index], 'collapsed', true);
                    }
                });
        },
        showAllContent: function () {
            this.toggleAllContentSectionsCollapse();
        },
    },
};
</script>
